import { useEffect, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import {
  BLANK_SHIPPING_ACCOUNT_FORM,
  type FieldErrors,
  type ShippingAccountFormFields,
  validateShippingAccountFields,
} from './checkout-domain'

export interface AddShippingAccountModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onSubmit: (fields: ShippingAccountFormFields) => Promise<void>
}

/**
 * Add-shipping-account modal for checkout §2 (UID-206, ADR-0067). Matches the
 * prototype's `shipAcctModal` (`Fictiv Platform.dc.html` lines 3667-3680):
 * Carrier, Account holder name, then a two-up Account number + Billing ZIP row.
 * Mirrors {@link import('./AddressFormModal').AddressFormModal}'s shape — local
 * fields/errors/busy, re-seed on open, `onSubmit` async that keeps the modal open
 * on reject (the caller toasts) and closes on success.
 *
 * The full account number is sent to the server on save and is NEVER echoed back
 * (the book only ever exposes a masked last-4). Add-only: checkout §2 has no
 * edit/delete/set-default affordance (those endpoints exist but are out of scope
 * for this panel — see the ADR).
 */
export function AddShippingAccountModal({ open, onClose, onSubmit }: AddShippingAccountModalProps) {
  const [fields, setFields] = useState<ShippingAccountFormFields>(BLANK_SHIPPING_ACCOUNT_FORM)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [busy, setBusy] = useState(false)

  // Reset the form each time the modal opens (add-only — no initial fields to
  // preserve, unlike AddressFormModal's edit mode). `open` is the only reactive
  // dependency; the body touches only stable setters + a module constant.
  useEffect(() => {
    if (open) {
      setFields(BLANK_SHIPPING_ACCOUNT_FORM)
      setErrors({})
    }
  }, [open])

  function updateField(name: keyof ShippingAccountFormFields, value: string) {
    setFields((current) => ({ ...current, [name]: value }))
    setErrors((current) => {
      if (!current[name]) return current
      const next = { ...current }
      delete next[name]
      return next
    })
  }

  async function handleSubmit() {
    const nextErrors = validateShippingAccountFields(fields)
    if (Object.values(nextErrors).some(Boolean)) {
      setErrors(nextErrors)
      return
    }
    setBusy(true)
    try {
      await onSubmit(fields)
      onClose()
    } catch {
      // Caller surfaces the failure via toast; keep the modal open so the buyer
      // can retry without re-typing.
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add shipping account"
      maxWidth={480}
      footer={
        <>
          <Button variant="outline" size="lg" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="lg" onClick={() => void handleSubmit()} loading={busy}>
            Save account
          </Button>
        </>
      }
    >
      <div className="checkout-address-form-fields">
        <Input
          label="Carrier"
          placeholder="FedEx, UPS, DHL…"
          value={fields.carrier}
          onChange={(event) => updateField('carrier', event.target.value)}
          error={errors.carrier}
        />
        <Input
          label="Account holder name"
          value={fields.holderName}
          onChange={(event) => updateField('holderName', event.target.value)}
        />
        <div className="checkout-shipping-account-form-row">
          <Input
            label="Account number"
            value={fields.accountNumber}
            onChange={(event) => updateField('accountNumber', event.target.value)}
            error={errors.accountNumber}
          />
          <Input
            label="Billing ZIP"
            value={fields.postalCode}
            onChange={(event) => updateField('postalCode', event.target.value)}
          />
        </div>
      </div>
    </Modal>
  )
}
